import { useMemo } from 'react';
import { t } from '@lingui/macro';
import _ from 'lodash';

import { useId } from '../../page-framework/panel/Panel';
import Panel from '../../page-framework/panel/PanelDecorator';
import { PANEL_CATEGORIES, LINE_CHART_PANEL_PREVIEW_IMAGE } from '../../page-framework/panel/PanelRegistry';
import PanelActions from '../../page-framework/panel/PanelActions';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import ChartTypeForm from '../../charts/ipm/ChartTypeForm';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { useTableSeries } from '../../charts/performance-chart/funcs/UseTableSeries';
import { hypervisorFilterId, timeRangeFilterId } from '../../filters';
import useChartDisplayType from '../../../utilities/UseChartDisplayType';

const title = t`Hypervisor Memory Usage: Top 5 VMs`;

const baseDataSource = {
    url: '/query/table',
    params: {
        type: 'server',
        subType: 'virtualmachine',
        host: true,
        rowStart: 0,
        rowLimit: 5,
    },
};

const PERCENT = 'PERCENT';
const BYTES = 'BYTES';

const typeConfig = {
    [PERCENT]: {
        yAxis: [
            {
                title: {
                    text: t`Utilization (%)`,
                },
                ceiling: 100,
            },
        ],
        chartType: 'areaspline',
        stacking: 'normal',
        tooltip: BuiltInTooltip,
        showGranularity: true,
        transforms: {
            syncAxisPoints: true,
        },
    },
    [BYTES]: {
        yAxis: [
            {
                title: {
                    text: t`Utilization (Bytes)`,
                },
                ceiling: undefined,
            },
        ],
        chartType: 'areaspline',
        stacking: 'normal',
        tooltip: BuiltInTooltip,
        showGranularity: true,
        transforms: {
            syncAxisPoints: true,
        },
    },
};

const baseTrend = {
    [PERCENT]: {
        formatter: FORMATTER_TYPES.PERCENT,
        units: '%',
        chartType: 'areaspline',
        key: 'vmMemPctUtil',
        multiplier: 1,
        forceDecimals: true,
    },
    [BYTES]: {
        formatter: FORMATTER_TYPES.BYTE,
        chartType: 'areaspline',
        key: 'vmActiveB',
        multiplier: 1,
        forceDecimals: true,
    },
};

function useDataSource(type) {
    return useMemo(() => {
        const metric = type === PERCENT ? 'vmMemPctUtil' : 'vmActiveB';
        return _.merge({}, baseDataSource, { params: { sort: `-${metric}`, metrics: [metric], trends: [metric] } });
    }, [type]);
}

function useChartConfig(type, data) {
    const chartTrends = useTableSeries(baseTrend[type], data, {
        nameMapping: 'server.name',
        colors: ['#63CE98', '#C8D87D', '#D641D8', '#4F134F', '#F7D4CF'],
    });

    return useMemo(() => {
        if (!chartTrends) return null;
        return {
            ...typeConfig[type],
            chartTrends,
        };
    }, [type, chartTrends]);
}

const selections = [
    { value: PERCENT, label: '%' },
    { value: BYTES, label: t`Bytes` },
];

function HypervisorVmMemoryBreakdownPanel() {
    const id = useId();
    const pageStateKey = `${id}-ChartType`;
    const [chartDisplayType, setChartDisplayType] = useChartDisplayType(pageStateKey, PERCENT);
    const dataSource = useDataSource(chartDisplayType);
    const data = usePanelDataService(dataSource);
    const chartData = data?.data; // Table data iterates over multiple rows (one per series)
    const chartConfig = useChartConfig(chartDisplayType, chartData);

    if (!data) return null;

    return (
        <>
            <PanelActions>
                <ChartTypeForm value={chartDisplayType} onChangeType={setChartDisplayType} selections={selections} />
            </PanelActions>
            <PerformanceChart chartDef={chartConfig} data={chartData} />
        </>
    );
}

HypervisorVmMemoryBreakdownPanel.propTypes = panelPropTypes;

HypervisorVmMemoryBreakdownPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'VmwareHostMemoryBreakdownTrend',
    catalogEntry: {
        category: PANEL_CATEGORIES.HYPERVISOR,
        title,
        description: t`Memory utilization trend for top 5 VMs on a selected hypervisor`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, hypervisorFilterId],
        optional: [],
    },
})(HypervisorVmMemoryBreakdownPanel);
